/**
 * Service-area pages for Dallas–Fort Worth.
 *
 * Each page is written for its own city: the local detail is real and public
 * (counties, landmarks, districts), and the page plan is for that city's
 * searches. KIT works remotely on US Central hours and has no office in these
 * cities; the pages say so plainly and never claim local clients or projects
 * we do not have. No LocalBusiness address is published for them.
 *
 * Priority follows the market plan: 1 = primary market, 2 = nearby service
 * areas, 3 = expansion areas.
 */

export type City = {
  slug: string;
  name: string;
  priority: 1 | 2 | 3;
  county: string;
  /** One or two true, specific sentences about the place, as an owner there would recognise it. */
  local: string;
  /** What that means for marketing a service business there. */
  angle: string;
  /** Neighbourhoods or districts a service-area page would name. */
  areas: string[];
  nearby: string[];
};

export const STATE = { name: 'Texas', abbr: 'TX' } as const;

export const CITIES: City[] = [
  {
    slug: 'dallas',
    name: 'Dallas',
    priority: 1,
    county: 'Dallas County',
    local: 'Dallas is the centre of the fourth-largest metro area in the US, and its summers regularly push past 100°F.',
    angle: 'With that much competition on every search, the businesses that win are the ones that answer first and show up in the map pack for the exact neighbourhood the customer is in.',
    areas: ['Uptown', 'Oak Cliff', 'Lake Highlands', 'Preston Hollow', 'Deep Ellum', 'Lakewood'],
    nearby: ['irving', 'garland', 'richardson', 'mesquite', 'addison'],
  },
  {
    slug: 'irving',
    name: 'Irving',
    priority: 2,
    county: 'Dallas County',
    local: 'Irving is home to the Las Colinas business district and sits right beside DFW International Airport.',
    angle: 'A mix of corporate campuses, apartments and family neighbourhoods means two kinds of customer: the commercial buyer who wants proof, and the homeowner who wants a fast answer.',
    areas: ['Las Colinas', 'Valley Ranch', 'Plymouth Park', 'Hackberry Creek'],
    nearby: ['dallas', 'grand-prairie', 'carrollton', 'addison'],
  },
  {
    slug: 'garland',
    name: 'Garland',
    priority: 2,
    county: 'Dallas County',
    local: 'Garland sits northeast of Dallas along the shore of Lake Ray Hubbard, with a large base of established single-family homes.',
    angle: 'Older homes mean repair, replacement and upgrade work, and owners who compare two or three companies on their phone before they call.',
    areas: ['Firewheel', 'Downtown Garland', 'Lake Ray Hubbard', 'Embree'],
    nearby: ['dallas', 'richardson', 'mesquite'],
  },
  {
    slug: 'richardson',
    name: 'Richardson',
    priority: 2,
    county: 'Dallas and Collin counties',
    local: 'Richardson is home to the Telecom Corridor and the University of Texas at Dallas, straddling Dallas and Collin counties.',
    angle: 'Customers here research carefully. Clear service pages, real reviews and honest answers to detailed questions win over flashy claims.',
    areas: ['Canyon Creek', 'CityLine', 'Breckinridge', 'Richardson Heights'],
    nearby: ['dallas', 'garland', 'plano', 'addison'],
  },
  {
    slug: 'mesquite',
    name: 'Mesquite',
    priority: 2,
    county: 'Dallas County',
    local: 'Mesquite lies east of Dallas along I-635 and I-30, and calls itself the Rodeo Capital of Texas.',
    angle: 'A practical, value-minded market: fast pages, a visible phone number and straight answers on price beat long brochure copy.',
    areas: ['Town East', 'Downtown Mesquite', 'Creek Crossing', 'Lawson'],
    nearby: ['dallas', 'garland'],
  },
  {
    slug: 'addison',
    name: 'Addison',
    priority: 2,
    county: 'Dallas County',
    local: 'Addison is a small town of about four square miles, known for its dense restaurant scene and its own airport.',
    angle: 'Small area, high competition: a service-area page has to be specific to Addison, not a renamed Dallas page, to stand a chance.',
    areas: ['Addison Circle', 'Vitruvian Park', 'Belt Line Road'],
    nearby: ['dallas', 'richardson', 'carrollton', 'irving'],
  },
  {
    slug: 'plano',
    name: 'Plano',
    priority: 3,
    county: 'Collin and Denton counties',
    local: 'Plano is home to Legacy West and the North American headquarters of several large companies, with a big base of newer suburban homes.',
    angle: 'Homeowners with higher job values who expect a polished site, online booking and quick confirmation.',
    areas: ['Legacy West', 'Willow Bend', 'Downtown Plano', 'Deerfield'],
    nearby: ['richardson', 'frisco', 'carrollton'],
  },
  {
    slug: 'frisco',
    name: 'Frisco',
    priority: 3,
    county: 'Collin and Denton counties',
    local: 'Frisco is one of the fastest-growing cities in the country, home to The Star, the Dallas Cowboys headquarters, and the PGA of America.',
    angle: 'New construction everywhere: the window to win a homeowner is often before they move in, which is exactly when content aimed at builders and new buyers works.',
    areas: ['The Star', 'Frisco Square', 'Starwood', 'Panther Creek'],
    nearby: ['plano', 'carrollton'],
  },
  {
    slug: 'carrollton',
    name: 'Carrollton',
    priority: 3,
    county: 'Dallas, Denton and Collin counties',
    local: 'Carrollton spans three counties, with a historic downtown square and one of the largest Korean-American communities in Texas.',
    angle: 'A diverse market where clear, simple language and fast mobile pages matter, and where word of mouth travels quickly through reviews.',
    areas: ['Downtown Carrollton', 'Josey Lane', 'Castle Hills', 'Trinity Mills'],
    nearby: ['addison', 'irving', 'plano', 'frisco'],
  },
  {
    slug: 'grand-prairie',
    name: 'Grand Prairie',
    priority: 3,
    county: 'Dallas, Tarrant and Ellis counties',
    local: 'Grand Prairie sits between Dallas and Fort Worth, along Joe Pool Lake, and stretches across three counties.',
    angle: 'Customers search from both sides of the metro, so pages need to make the service area obvious and the booking path short.',
    areas: ['Lake Ridge', 'Westchester', 'Downtown Grand Prairie', 'Mira Lagos'],
    nearby: ['irving', 'arlington', 'dallas'],
  },
  {
    slug: 'arlington',
    name: 'Arlington',
    priority: 3,
    county: 'Tarrant County',
    local: 'Arlington is home to AT&T Stadium, Globe Life Field, Six Flags Over Texas and the University of Texas at Arlington.',
    angle: 'A large city in its own right, between Dallas and Fort Worth, where Arlington-specific pages beat generic "DFW" pages on local searches.',
    areas: ['Entertainment District', 'Viridian', 'South Arlington', 'Dalworthington Gardens'],
    nearby: ['grand-prairie', 'dallas'],
  },
];

export const city = (slug: string) => CITIES.find((c) => c.slug === slug);

/** The HVAC page plan for a city: what people need, then where they need it. */
export function servicePagePlan(c: City) {
  return [
    { page: 'AC repair', keyword: `AC repair ${c.name}` },
    { page: 'AC installation', keyword: `AC installation ${c.name}` },
    { page: 'AC replacement', keyword: `AC replacement ${c.name}` },
    { page: 'Heating repair', keyword: `heating repair ${c.name}` },
    { page: 'Furnace installation', keyword: `furnace installation ${c.name}` },
    { page: 'HVAC maintenance', keyword: `HVAC maintenance ${c.name}` },
    { page: 'Emergency HVAC', keyword: `emergency HVAC repair ${c.name}` },
  ];
}

export function supportingTopics(c: City) {
  return [
    `Why ${c.name} homes struggle to stay cool in extreme summer heat`,
    'How often should you service an HVAC system in Texas?',
    'Repair or replace: when does an older AC unit need replacing?',
    'What to do when your AC stops cooling during a heatwave',
  ];
}

/** How we decide which city pages to build first. Scored 1 to 5 each. */
export const CITY_CRITERIA = [
  { id: 'coverage', label: 'Actual service coverage', help: 'Does your team really go there?' },
  { id: 'customers', label: 'Existing customers', help: 'Have you done jobs there already?' },
  { id: 'demand', label: 'Search demand', help: 'Are people searching for your service there?' },
  { id: 'competition', label: 'Competition (5 = easy)', help: 'How hard will it be to compete?' },
  { id: 'value', label: 'Commercial value', help: 'Are the jobs worth pursuing?' },
  { id: 'travel', label: 'Travel distance (5 = close)', help: 'Can you serve it profitably?' },
] as const;
